import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PlayCircle, Clock } from "lucide-react";

const SCRIPT = [
  {
    section: "Opening — The Land",
    duration: "0:00 – 0:25",
    voiceover: "Azure Gypsy Farms. A network built around the people who actually grow your food. Real food. Real hobby farmers. Straight to your door.",
    visual: "Sun-drenched fields, hands in soil, a basket of just-picked produce. Logo fades in.",
  },
  {
    section: "For Hobby Farmers — Sign Up",
    duration: "0:25 – 1:10",
    voiceover: "If you grow it, raise it, or harvest it — we built this for you. Hobby farmers sign up in minutes. Sell what's ready, give away what's beautifully imperfect, barter what you have plenty of, or set up direct wholesale contracts. You keep your payment system. You set your prices. We just connect you to the families who want what you grow.",
    visual: "A farmer scrolls the dashboard on a tablet near the chicken coop. Listings appearing: tomatoes, eggs, ribeye, mutation carrots labeled 'free'.",
  },
  {
    section: "For Delivery Drivers — Hiring",
    duration: "1:10 – 1:55",
    voiceover: "We're hiring drivers across every state. Pick up from local hobby farmers, drop off to clients in your community. Honest local-route pay, plus one hundred percent of every tip. Cash out through PayPal or CashApp. And if a hobby farmer says yes, ask them for free food. We're a community, after all.",
    visual: "Driver loads coolers into a pickup truck. Phone shows live route map. Tip and earnings screen.",
  },
  {
    section: "For Wholesale — Bulk Buying",
    duration: "1:55 – 2:25",
    voiceover: "Stores, families pooling together, community groups — buy in bulk directly from hobby farmers. No middlemen, no markup games. Apply for wholesale onboarding and our team will get you set up with the growers in your state.",
    visual: "Boxes of greens labeled 'Wholesale' loaded into a van. Family unpacks a big crate at home.",
  },
  {
    section: "Membership — Free Ordering",
    duration: "2:25 – 2:50",
    voiceover: "Members order for free across the whole marketplace. Without a membership, modest service fees apply. Yearly membership is just $89.99 right now in our founders' promo — simple, honest, and easy to sign up.",
    visual: "Pricing card animation: 'Members: Free Ordering' vs 'Non-members: small service fees'. Friendly check mark, no clutter.",
  },
  {
    section: "Close — Join the Network",
    duration: "2:50 – 3:00",
    voiceover: "Azure Gypsy Farms. Real food. Real hobby farmers. Real neighbors. Join us.",
    visual: "Group shot — farmer, driver, family — at a farm gate. Logo + tagline.",
  },
];

// REPLACE THIS WITH YOUR ACTUAL VIDEO URL WHEN PRODUCED.
// Examples:
//   YouTube: "https://www.youtube.com/embed/YOUR_VIDEO_ID"
//   Vimeo:   "https://player.vimeo.com/video/YOUR_VIDEO_ID"
//   MP4:     "https://your-cdn.com/explainer.mp4"
const VIDEO_URL = ""; // empty = show "coming soon + script" view

export default function VideoSection() {
  const [open, setOpen] = useState(false);
  const isYouTube = VIDEO_URL.includes("youtube") || VIDEO_URL.includes("youtu.be");
  const isVimeo = VIDEO_URL.includes("vimeo");

  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-12 py-24" data-testid="video-section">
      <div className="grid md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-4">
          <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-800 font-semibold">In 3 Minutes</div>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-stone-900 leading-tight">
            See how it all works.
          </h2>
          <p className="mt-5 font-sans text-stone-600 leading-relaxed text-base">
            A short walkthrough of hobby farmer sign-up, driver hiring, wholesale bulk buying, and our free-ordering membership.
          </p>
          <div className="mt-6 flex items-center gap-2 text-stone-500 text-xs font-sans">
            <Clock size={14} /> 3 min watch
          </div>
        </div>

        <div className="md:col-span-8">
          {VIDEO_URL ? (
            <div className="aspect-video rounded-md overflow-hidden border border-stone-200 bg-stone-100">
              {isYouTube || isVimeo ? (
                <iframe
                  data-testid="explainer-iframe"
                  src={VIDEO_URL}
                  title="Azure Gypsy Farms — 3 minute explainer"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <video data-testid="explainer-video" src={VIDEO_URL} controls className="w-full h-full" />
              )}
            </div>
          ) : (
            <button
              data-testid="play-explainer-btn"
              onClick={() => setOpen(true)}
              className="group relative aspect-video w-full rounded-md overflow-hidden border border-stone-200 bg-stone-900"
            >
              <img
                src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1600&q=80"
                alt="Hobby farmer fields"
                className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-70 transition-opacity"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/80 via-stone-900/20 to-transparent" />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-stone-50 gap-3">
                <PlayCircle size={72} strokeWidth={1} className="group-hover:scale-110 transition-transform" />
                <div className="font-serif text-2xl">Watch the 3-min walkthrough</div>
                <div className="text-xs uppercase tracking-[0.3em] text-amber-200">Script Ready · Video Coming Soon</div>
              </div>
            </button>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-stone-50 max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">3-Minute Explainer — Script</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-600 font-sans">
            Ready-to-produce voiceover script with timing and visuals. Paste your produced video URL into <code className="bg-stone-200 px-1 rounded">VIDEO_URL</code> in <code className="bg-stone-200 px-1 rounded">VideoSection.jsx</code> to replace this card with the live player.
          </p>
          <div className="space-y-5 mt-4">
            {SCRIPT.map((s, i) => (
              <div key={i} data-testid={`script-section-${i}`} className="bg-white border border-stone-200 rounded-md p-5">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-serif text-xl text-stone-900">{s.section}</div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-800 font-semibold">{s.duration}</div>
                </div>
                <div className="mt-3 text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Voiceover</div>
                <p className="mt-1 text-sm text-stone-700 font-sans leading-relaxed">{s.voiceover}</p>
                <div className="mt-3 text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Visual</div>
                <p className="mt-1 text-sm text-stone-600 font-sans italic">{s.visual}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
